import NiceModal, { useModal } from "@ebay/nice-modal-react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCustomModel } from "components/context/CustomProvider";
import useCustomDataSource from "components/ui/customs/useCustomDataSource";
import { useContext, useEffect, useState } from "react";
import ReplayIcon from "@mui/icons-material/Replay";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomDialog from "components/ui/customs/CustomDialog";
import { SnackbarContext } from "components/context/SnackbarProvider";

const CreateExcelYearModal = NiceModal.create(({ deal }) => {
  const modal = useModal();
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [withOldData, setWithOldData] = useState(false);

  return (
    <CustomDialog
      open={modal.visible}
      onClose={modal.remove}
      width="xs"
      maxWidth=""
      fullWidth={false}
    >
      <CustomDialog title>Create Excel Year for {deal.name}</CustomDialog>
      <CustomDialog content>
        <TextField
          label="Year"
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={withOldData}
              onChange={(e) => setWithOldData(e.target.checked)}
            />
          }
          label="Add previous full year data"
        />
      </CustomDialog>
      <CustomDialog actions>
        <Button onClick={modal.remove} color="primary">
          Cancel
        </Button>
        <Button
          onClick={() => {
            modal.resolve({ dealId: deal.id, year, withOldData });
            modal.remove();
          }}
          color="primary"
        >
          Create
        </Button>
      </CustomDialog>
    </CustomDialog>
  );
});

const FinancialsImportWidget = ({ deal }) => {
  const model = useCustomModel("backoffice.Deal");
  const updateModal = model.updateModal;
  const { useImportData, useCreateExcelYear, useValidateAll } =
    useCustomDataSource("backoffice.Deal");
  const { mutateAsync: importDataAsync, error, status } = useImportData();
  const { mutateAsync: createExcelYearAsync } = useCreateExcelYear();
  const { mutateAsync: validateAll } = useValidateAll();
  const [anchorEl, setAnchorEl] = useState(null);
  const [importation, setImportation] = useState(null);
  const { displayMessage, displayError } = useContext(SnackbarContext);

  const importData = () => {
    if (!deal.id) {
      displayError("No deal selected for import.");
      return;
    }
    return importDataAsync({
      dealId: deal.id,
    })
      .then((data) => {
        setImportation(data);
      })
      .catch((err) => {
        console.error("Import Data error:", err);
        displayError(err);
      });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenSheet = () => {
    window.open(
      `https://docs.google.com/spreadsheets/d/${deal.gs_source_id}`,
      "_blank",
    );
    handleClose();
  };

  const handleValidate = () => {
    validateAll({
      dealId: deal.id,
    })
      .then((res) => {
        displayMessage(res.status);
        setImportation(null);
        importData();
        handleClose();
      })
      .catch((err) => {
        displayError(err);
      });
    handleClose();
  };

  const handleCreateExcelYear = () => {
    NiceModal.show(CreateExcelYearModal, { deal })
      .then(createExcelYearAsync)
      .then((res) => displayMessage(res.status))
      .catch((err) => {
        displayError("Create Excel Year error:", err);
      });
    handleClose();
  };

  useEffect(() => {
    importData();
  }, [deal.id]);

  const menuItems = [
    {
      label: "Open Sheet",
      action: handleOpenSheet,
      disabled: !deal.gs_source_id,
    },
    {
      label: "Validate All",
      action: handleValidate,
      disabled:
        status !== "success" || (importation?.nb_proposed_value || 0) === 0,
    },
    {
      label: "Create excel year",
      disabled: !deal.gs_source_id,
      action: handleCreateExcelYear,
    },
  ];

  const defaultChipProps = {
    variant: "outlined",
    onClick: handleClick,
    color: "primary",
  };

  let chip = <Chip {...defaultChipProps} label="..." />;

  if (status === "loading" || status === "pending") {
    chip = (
      <Chip
        {...defaultChipProps}
        icon={
          <ReplayIcon
            sx={{
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        }
        label="Loading..."
      />
    );
  }

  if (status === "error") {
    chip = (
      <Tooltip title={error.response?.data?.detail || error.message}>
        <Chip
          {...defaultChipProps}
          icon={<ErrorIcon />}
          onDelete={importData}
          deleteIcon={<ReplayIcon />}
          color="error"
          label="Error"
        />
      </Tooltip>
    );
  }

  const count = importation?.nb_proposed_value;
  if (status === "success" && count === 0) {
    chip = (
      <Chip
        {...defaultChipProps}
        icon={<CheckCircleIcon />}
        onDelete={importData}
        deleteIcon={<ReplayIcon />}
        label="Synchronized"
        color="default"
      />
    );
  }

  if (status === "success" && count > 0) {
    chip = (
      <Chip
        {...defaultChipProps}
        icon={<CheckCircleIcon />}
        onDelete={importData}
        deleteIcon={<ReplayIcon />}
        label={count + " data to validate"}
        color={count > 0 ? "success" : "default"}
      />
    );
  }

  return (
    <>
      {chip}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {menuItems.map(({ label, action, disabled }) => (
          <MenuItem key={label} onClick={action} disabled={disabled}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default FinancialsImportWidget;
